import React, { useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { PiUserListLight, PiXLight, PiPlusLight } from "react-icons/pi";
import Dropdown, { DropdownOption } from "./TailwindComponents/Dropdown";
import Button from "./TailwindComponents/Button";

export interface ClientSelectorClient {
  ClientID: string;
  ClientRoles: string[];
}

export interface ClientSelectorProps {
  value?: ClientSelectorClient[];
  onChange?: (clients: ClientSelectorClient[]) => void;
  clients?: ClientSelectorClient[];
  onClientsChange?: (clients: ClientSelectorClient[]) => void;
  clientOptions: DropdownOption[];
  roleOptions: DropdownOption[];
  onAddNewClient?: () => void;
  autoAddClient?: ClientSelectorClient | null;
  title?: string;
  description?: string;
}

const ClientSelector: React.FC<ClientSelectorProps> = ({
  value,
  onChange,
  clients,
  onClientsChange,
  clientOptions,
  roleOptions,
  onAddNewClient,
  autoAddClient,
  title,
  description,
}) => {
  const { t } = useTranslation();
  const lastAutoAddedRef = useRef<string | null>(null);

  const clientsList = value || clients || [];
  const handleChange = onChange || onClientsChange;

  // Derive selectedClientIds directly — no local state, no sync useEffect, no double-render
  const selectedClientIds = useMemo(() => clientsList.map(c => c.ClientID), [clientsList]);

  // Auto-add a newly created client when passed from parent
  useEffect(() => {
    if (autoAddClient && autoAddClient.ClientID && autoAddClient.ClientID !== lastAutoAddedRef.current) {
      lastAutoAddedRef.current = autoAddClient.ClientID;
      if (!clientsList.some(c => c.ClientID === autoAddClient.ClientID)) {
        const updatedClients = [...clientsList, autoAddClient];
        if (handleChange) {
          handleChange(updatedClients);
        }
      }
    }
  }, [autoAddClient]);

  const handleAddClient = useCallback((clientId: string) => {
    if (!selectedClientIds.includes(clientId)) {
      const newClient: ClientSelectorClient = {
        ClientID: clientId,
        ClientRoles: []
      };
      const updatedClients = [...clientsList, newClient];
      if (handleChange) {
        handleChange(updatedClients);
      }
    }
  }, [selectedClientIds, clientsList, handleChange]);

  const handleRemoveClient = useCallback((clientId: string) => {
    const updatedClients = clientsList.filter(c => c.ClientID !== clientId);
    if (handleChange) {
      handleChange(updatedClients);
    }
  }, [clientsList, handleChange]);

  const handleChangeRoles = useCallback((clientId: string, newRoles: string | string[]) => {
    const rolesArray = Array.isArray(newRoles) ? newRoles : [newRoles];
    const updatedClients = clientsList.map(c =>
      c.ClientID === clientId ? { ...c, ClientRoles: rolesArray } : c
    );
    if (handleChange) {
      handleChange(updatedClients);
    }
  }, [clientsList, handleChange]);

  const availableClientOptions = useMemo(() => {
    return clientOptions.filter(opt => !selectedClientIds.includes(opt.value));
  }, [clientOptions, selectedClientIds]);

  const getClientLabel = (clientId: string) => {
    return clientOptions.find(opt => opt.value === clientId)?.label || clientId;
  };

  return (
    <div className="rounded p-1">
      <div className="mb-3 flex items-center gap-2">
        <PiUserListLight className="h-5 w-5 text-primary-600" />
        <div className="text-sm font-light">{title || t('Select clients')}</div>
      </div>

      {description && (
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">{description}</p>
      )}

      <div className="space-y-2">
        <Dropdown
          id="client-selector-main"
          options={availableClientOptions}
          onChange={(value) => {
            if (value) {
              handleAddClient(String(value));
            }
          }}
          placeholder={t('Select a client')}
          width="w-full"
        />

        {clientsList.length > 0 && (
          <div className="glass space-y-2 mt-3 p-3 rounded-lg">
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {t('Added Clients')} ({clientsList.length})
            </p>
            <div className="space-y-2">
              {clientsList.map((client) => (
                <div
                  key={client.ClientID}
                  className="flex items-center justify-between gap-3 p-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-gray-900 dark:text-gray-100 block truncate">
                      {getClientLabel(client.ClientID)}
                    </span>
                  </div>

                  <div className="flex gap-2 items-center flex-shrink-0">
                    <Dropdown
                      id={`client-role-${client.ClientID}`}
                      options={roleOptions}
                      onSelect={(option) => {
                        handleChangeRoles(client.ClientID, option.value);
                      }}
                      value={client.ClientRoles}
                      placeholder={t('Roles')}
                      width="w-32"
                      thinner
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="small"
                      onClick={() => handleRemoveClient(client.ClientID)}
                      ariaLabel={t('Remove client')}
                    >
                      <PiXLight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Soft warning: client added without a role */}
        {clientsList.some(
          (c) =>
            c.ClientID &&
            String(c.ClientID).trim() !== '' &&
            (!c.ClientRoles || c.ClientRoles.length === 0)
        ) && (
          <p className="text-xs text-warning-600 dark:text-warning-400 flex items-center gap-1.5 px-1">
            <PiUserListLight className="w-3.5 h-3.5 flex-shrink-0" />
            {t('One or more clients have no role assigned. A default role will be used - you can update roles later.')}
          </p>
        )}

        {onAddNewClient && (
          <Button
            variant="primary"
            size="small"
            onClick={onAddNewClient}
            type="button"
            className="ml-auto"
          >
            <PiPlusLight /> {t('Add New Client')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ClientSelector;
